import { isDefined } from '@xemplo/type-guard';
import { Company, Navigation, NavigationApiModel, PersonaExtended } from '@xemplo/types';

export function mapNavData(data: NavigationApiModel): Navigation {
  const personas = data.personas
    .map(makeDefaultUrl)
    .filter((p) => p.defaultUrl != null)
    .sort(byCompanyName);

  const personaLookup = Object.fromEntries(personas.map((p) => [p.id, p]));

  // Service-provider-linked personas group under their service provider; every
  // other persona (instance-based and global) groups by its instance.id.
  const instancesLookup = Object.fromEntries(
    personas
      .filter((p) => p.serviceProviderId == null && p.instance != null)
      .map((p) => [p.instance.id, p.instance] as const),
  );
  const instances = Object.values(instancesLookup).sort(byName);

  const serviceProvidersLookup = Object.fromEntries(
    personas
      .map((p) => p.serviceProvider)
      .filter((sp) => isDefined(sp))
      .map((sp) => [sp.id, sp] as const),
  );
  const serviceProviders = Object.values(serviceProvidersLookup).sort(byName);

  const companies = personas.map((p) => p.company).filter(Boolean) as Company[];
  const companiesLookup = Object.fromEntries(companies.map((c) => [c.id, c]));

  return {
    user: data.user,
    personas,
    personaLookup,
    instances,
    instancesLookup,
    serviceProviders,
    serviceProvidersLookup,
    companies,
    companiesLookup,
  };
}

/**
 * The logged in user may have multiple personas within the same company name
 * and some may not have a company name at all. This function sorts the personas
 * by company name, with those without a company name coming last.
 */
function byCompanyName(a: PersonaExtended, b: PersonaExtended) {
  if (a.company?.name && b.company?.name) {
    // If both have company names, sort by company name
    return a.company.name.localeCompare(b.company.name);
  }
  if (a.company?.name) {
    // If only 'a' has a company name, it should come before 'b'
    return -1;
  }
  if (b.company?.name) {
    // If only 'b' has a company name, it should come before 'a'
    return 1;
  }
  // If neither has a company name, they are considered equal in terms of sorting
  return 0;
}

function byName<T extends { name: string }>(a: T, b: T) {
  return a.name.localeCompare(b.name);
}

function makeDefaultUrl(persona: PersonaExtended) {
  if (persona.defaultUrl) return persona;

  return {
    ...persona,
    defaultUrl: persona.items[0]?.url ?? persona.instance?.modules[0]?.url,
  };
}
